/* eslint-disable complexity */
import React, { useMemo } from 'react';
import { formatCurrency, useLocale } from '@unisporkal/localization';
import PropTypes from 'prop-types';
import { filmSizeType, imageSizeType } from '../../../../types/sizeType';
import acquisitionTypes from '../../../../support/acquisitionTypes';
import { useAcquisitionType } from '../../../../hooks/stateful/useAcquisitionType';
import useOnLoad from '../../../../hooks/queries/useOnLoad';
import styles from '../AssetSizes.module.scss';
import UltrapackPriceInfo from './UltrapackPriceInfo';

const PriceInfo = ({ size, currencyCode = 'USD' }) => {
  const locale = useLocale();
  const { response: { config: { hasPreferredPricingVisible } = {} } = {} } =
    useOnLoad();
  const { acquisitionType } = useAcquisitionType();

  // Allow price and gst to be styled separately
  const priceAndGeoTax = (price = size.rawPrice, options = {}) => {
    if (!price) return null;
    const { formattedPrice, geoTaxDisclosure } = formatCurrency(
      price,
      locale,
      currencyCode,
      {
        separateGeoTaxFromPrice: true,
        ...options,
      }
    );
    return { formattedPrice, geoTaxDisclosure };
  };

  const renderPrice = (price, testId) => (
    <div
      className={styles.price}
      data-testid={testId}
    >
      {price.formattedPrice}
      {price.geoTaxDisclosure ? (
        <span className={styles.geoTax}>{price.geoTaxDisclosure}</span>
      ) : null}
    </div>
  );

  const ultraPackPrice = useMemo(() => {
    if (acquisitionType === acquisitionTypes.ULTRA_PACK_UPSELL) {
      return priceAndGeoTax(size.pricePerDownload, {
        minimumFractionDigits: 0,
      });
    }
    return { formattedPrice: '', geoTaxDisclosure: null };
  }, [acquisitionType, size.pricePerDownload, locale, currencyCode]);

  if (ultraPackPrice?.formattedPrice) {
    return (
      <UltrapackPriceInfo
        formattedPrice={ultraPackPrice.formattedPrice}
        geoTaxDisclosure={ultraPackPrice?.geoTaxDisclosure}
      />
    );
  }

  if (
    acquisitionType === acquisitionTypes.LIMITED_USE_PRODUCTS ||
    acquisitionType === acquisitionTypes.MODIFIED_LIMITED_USE_PRODUCTS
  ) {
    const { formattedPrice } = formatCurrency(
      size.price,
      locale,
      currencyCode,
      { minimumFractionDigits: 0 }
    );
    return renderPrice({ formattedPrice }, 'limited-use-price');
  }

  if (hasPreferredPricingVisible && size.discountPrice) {
    const discountedPrice = priceAndGeoTax();
    // Geotax is not shown for original price
    const originalPrice = priceAndGeoTax(size.rawOriginalPrice);

    return (
      <div className={styles.price}>
        <div className={styles.preferred}>
          {size.discountPrice && (
            <div
              className={styles.original}
              data-testid="original-price"
            >
              {originalPrice?.formattedPrice}
            </div>
          )}
          <div
            className={styles.discounted}
            data-testid="discounted-price"
          >
            {discountedPrice?.formattedPrice}
            <span className={styles.geoTax}>
              {discountedPrice?.geoTaxDisclosure}
            </span>
          </div>
        </div>
      </div>
    );
  }

  const regularPrice = priceAndGeoTax();
  if (!regularPrice) return null;

  return renderPrice(regularPrice, 'regular-price');
};

PriceInfo.propTypes = {
  size: PropTypes.oneOfType([imageSizeType, filmSizeType]).isRequired,
  currencyCode: PropTypes.string,
};

export default PriceInfo;
